import type { FeatureFlagValue } from '@posthog/core';
import IFlagsmith from 'flagsmith';
import { Flags } from 'flagsmith-nodejs';
import { PostHog } from 'posthog-js';
import React from 'react';

// sort flags alphabetically to match flag-smith order
export const DefaultFlagsContext = {
  // These two are permanent flags, we use these to rollout flags
  // to our dogfood and launchpad segments.
  EnableAlphaApps: false,
  EnableBetaApps: false,

  EnableManyCompanies: true, // Permanently enabled - no longer checks Flagsmith
  EnableFolderLocking: false,
  EnableRootFolderLocking: false,
  EnableSubscriptionEditSummary: false,
  EnableAppSetup: false,
  EnableAppSetupUIUpdates: false,
  // PostHog flags
  EnableBillingConsolidation: false,
  EnableInvoiceStatusNotifications: false,
};

export type FlagContextType = typeof DefaultFlagsContext;

export const getServerFlagsContextValue = (
  flagsmithFlags: Flags,
  posthogFlags: Record<string, FeatureFlagValue>,
) => ({
  EnableAlphaApps: flagsmithFlags.isFeatureEnabled('enable_alpha_apps'),
  EnableBetaApps: flagsmithFlags.isFeatureEnabled('enable_beta_apps'),
  EnableManyCompanies: true, // Permanently enabled - no longer checks Flagsmith
  EnableFolderLocking: flagsmithFlags.isFeatureEnabled('enable_folder_locking'),
  EnableRootFolderLocking: flagsmithFlags.isFeatureEnabled(
    'enable_root_folder_locking',
  ),
  EnableSubscriptionEditSummary: flagsmithFlags.isFeatureEnabled(
    'subscription_edit_summary',
  ),
  EnableAppSetup: flagsmithFlags.isFeatureEnabled('enable_app_setup'),
  EnableAppSetupUIUpdates: flagsmithFlags.isFeatureEnabled(
    'enable_app_setup_ui_updates',
  ),
  // posthog flags
  EnableBillingConsolidation: Boolean(posthogFlags['billing-consolidation']),
  EnableInvoiceStatusNotifications: Boolean(
    posthogFlags['enable-invoice-status-notifications'],
  ),
});

export const getClientFlagsContextValue = (
  flagsmithFlags: typeof IFlagsmith,
  posthogFlags: PostHog,
) => ({
  EnableAlphaApps: flagsmithFlags.hasFeature('enable_alpha_apps'),
  EnableBetaApps: flagsmithFlags.hasFeature('enable_beta_apps'),
  EnableManyCompanies: true, // Permanently enabled - no longer checks Flagsmith
  EnableFolderLocking: flagsmithFlags.hasFeature('enable_folder_locking'),
  EnableRootFolderLocking: flagsmithFlags.hasFeature(
    'enable_root_folder_locking',
  ),
  EnableSubscriptionEditSummary: flagsmithFlags.hasFeature(
    'subscription_edit_summary',
  ),
  EnableAppSetup: flagsmithFlags.hasFeature('enable_app_setup'),
  EnableAppSetupUIUpdates: flagsmithFlags.hasFeature(
    'enable_app_setup_ui_updates',
  ),
  // PostHog flags
  EnableBillingConsolidation: posthogFlags.isFeatureEnabled(
    'billing-consolidation',
  ),
  EnableInvoiceStatusNotifications: posthogFlags.isFeatureEnabled(
    'enable-invoice-status-notifications',
  ),
});

export const FlagsContext = React.createContext(DefaultFlagsContext);
